import { message } from 'antd';
import useSmartField from 'hooks/useSmartField';
import produce from 'immer';
import { uniq } from 'lodash-es';
import { useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { FV, PlatformConfig, RegionConfig, Store } from './share';
import { useGetRegionLabel } from 'hooks/business/useRegion';

function useCheck() {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const {
    field: { value, onChange },
  } = useSmartField<FV['storeScopes']>('storeScopes');

  const {
    field: { value: storeMap, onChange: onStoreMapChange },
  } = useSmartField<FV['assets']['storeMap']>('assets.storeMap');

  const {
    field: { value: wabaMap, onChange: onWabaMapChange },
  } = useSmartField<FV['assets']['wabaMap']>('assets.wabaMap');

  interface CheckChunk {
    idList: number[];
    platform: General.PlatformUpperCaseTunnel;
    region: General.UpperCaseRegion | 'all';
  }

  interface CheckItem {
    id: number;
    platform: General.PlatformUpperCaseTunnel;
    region: General.UpperCaseRegion | 'all';
  }

  const onChecked = useCallback(
    (checked: boolean, checkItems: CheckItem[]) => {
      const chunkList = checkItems.reduce<CheckChunk[]>((res, { id, platform, region }) => {
        const target = res.find((chunk) => chunk.platform === platform && chunk.region === region);

        if (target) {
          target.idList.push(id);
        } else {
          res.push({
            platform,
            region,
            idList: [id],
          });
        }

        return res;
      }, []);

      const warnedMap: Partial<
        Record<General.PlatformUpperCaseTunnel | General.UpperCaseRegion | 'all', true>
      > = {};

      const next = produce(value, (value) => {
        chunkList.forEach(({ idList, platform, region }) => {
          const platformTargetIndex = value.findIndex(
            (platformScope) => platformScope.platform === platform,
          );
          const platformTarget = value[platformTargetIndex];

          const checkedRegionScope = {
            region,
            storeCount: idList.length,
            storeIds: idList,
          };

          if (!platformTarget) {
            if (checked) {
              value.push({
                platform,
                storeCount: idList.length,
                regionStoreScopes: [checkedRegionScope],
              });
            }
          } else {
            const regionTargetIndex = platformTarget.regionStoreScopes.findIndex(
              (regionScope) => regionScope.region === region,
            );
            const regionTarget = platformTarget.regionStoreScopes[regionTargetIndex];

            if (!regionTarget) {
              if (checked) {
                platformTarget.regionStoreScopes.push(checkedRegionScope);
              } else {
                const isPlatformChecked = platformTarget.regionStoreScopes.some((regionScope) => {
                  return regionScope.region === 'all' && (regionScope.storeIds || []).length === 0;
                });

                // 平台全选时，不可选择单个店铺
                if (isPlatformChecked) {
                  if (!warnedMap[platform]) {
                    warnedMap[platform] = true;

                    message.warn(
                      t('department.已选择platform全部，请先取消选中', {
                        platform: platformLabelMap[toLowerCase(platform)],
                      }),
                    );
                  }
                }
              }
            } else if (checked) {
              regionTarget.storeIds = uniq([...(regionTarget.storeIds || []), ...idList]);
              regionTarget.storeCount = regionTarget.storeIds.length;
            } else {
              const isRegionChecked = (regionTarget.storeIds || []).length === 0;

              // 站点全选时，不可选择单个店铺
              if (isRegionChecked) {
                if (!warnedMap[region]) {
                  warnedMap[region] = true;

                  message.warn(
                    region === 'all'
                      ? t('department.已选择platform全部，请先取消选中', {
                          platform: platformLabelMap[toLowerCase(platform)],
                        })
                      : t('department.已选择platformregion全部，请先取消选中', {
                          platform: platformLabelMap[toLowerCase(platform)],
                          region: getRegionLabel(region),
                        }),
                  );
                }
              } else {
                regionTarget.storeIds = (regionTarget.storeIds || []).filter(
                  (id) => !idList.some((_id) => String(id) === String(_id)),
                );
                regionTarget.storeCount = regionTarget.storeIds.length;
              }

              if (regionTarget.storeCount === 0) {
                platformTarget.regionStoreScopes.splice(regionTargetIndex, 1);

                if (platformTarget.regionStoreScopes.length === 0) {
                  value.splice(platformTargetIndex, 1);
                }
              }
            }
          }
        });
      });

      onChange(next);
    },
    [value, onChange, t, getRegionLabel],
  );

  const onStoreChecked = useCallback(
    (checked: boolean, storeList: Store[]) => {
      const nextStoreMap = storeList.reduce(
        (res, store) => {
          res[store.id] = store;

          return res;
        },
        { ...storeMap },
      );

      onStoreMapChange(nextStoreMap);

      const checkItems = storeList.map(({ id, externalType, region }) => {
        return {
          id,
          platform: externalType,
          region,
        };
      });

      onChecked(checked, checkItems);
    },
    [storeMap, onStoreMapChange, onChecked],
  );

  const onWabaChecked = useCallback(
    (checked: boolean, wabaList: WhatsappBusinessApi.Account[]) => {
      const nextWabaMap = wabaList.reduce(
        (res, waba) => {
          res[waba.tunnelAccountId] = waba;

          return res;
        },
        { ...wabaMap },
      );

      onWabaMapChange(nextWabaMap);

      const checkItems = wabaList.map(({ tunnelAccountId }) => {
        return {
          id: Number(tunnelAccountId),
          platform: 'WA_BIZ',
          region: 'all',
        } as const;
      });

      onChecked(checked, checkItems);
    },
    [wabaMap, onWabaMapChange, onChecked],
  );

  const isChecked = useCallback(
    (props: CheckChunk) => {
      const { idList, platform, region } = props;

      return value.some((platformScope) => {
        if (platformScope.platform !== platform) {
          return false;
        }

        return platformScope.regionStoreScopes.some((regionScope) => {
          if (regionScope.region === 'all' && (regionScope.storeIds || []).length === 0) {
            return true;
          }

          if (regionScope.region !== region) {
            return false;
          }

          return (
            (regionScope.storeIds || []).length === 0 ||
            idList.every((id) =>
              (regionScope.storeIds || []).some((_id) => String(id) === String(_id)),
            )
          );
        });
      });
    },
    [value],
  );

  const isIndeterminate = useCallback(
    (props: CheckChunk) => {
      const { idList, platform, region } = props;

      return value.some((platformScope) => {
        if (platformScope.platform !== platform) {
          return false;
        }

        return platformScope.regionStoreScopes.some((regionScope) => {
          if (regionScope.region === 'all' || regionScope.region !== region) {
            return false;
          }

          return idList.some((id) =>
            (regionScope.storeIds || []).some((_id) => String(_id) === String(id)),
          );
        });
      });
    },
    [value],
  );

  const isPlatformChecked = useCallback(
    (platformConfig: PlatformConfig) => {
      const { platform, regionList, count } = platformConfig;

      return value.some((platformScope) => {
        if (platformScope.platform !== platform) {
          return false;
        }

        const checkAll = platformScope.regionStoreScopes.some((regionScope) => {
          return regionScope.region === 'all' && (regionScope.storeIds || []).length === 0;
        });

        if (checkAll) {
          return true;
        }

        if (regionList.length === 0) {
          const target = platformScope.regionStoreScopes.find(({ region }) => region === 'all');

          if (target && (target.storeIds || []).length === count) {
            return true;
          }
        }

        return (
          platformScope.regionStoreScopes.length === regionList.length &&
          platformScope.regionStoreScopes.every((regionScope) => {
            const regionCheckAll = (regionScope.storeIds || []).length === 0;

            if (regionCheckAll) {
              return true;
            }

            const regionConfig = regionList.find(
              (regionConfig) => regionConfig.region === regionScope.region,
            );

            if (regionConfig && regionConfig.count === (regionScope.storeIds || []).length) {
              return true;
            }

            return false;
          })
        );
      });
    },
    [value],
  );

  const isPlatformIndeterminate = useCallback(
    (platform: General.PlatformUpperCaseTunnel) => {
      return value.some((platformScope) => platformScope.platform === platform);
    },
    [value],
  );

  const isRegionChecked = useCallback(
    (platform: General.PlatformUpperCaseTunnel, regionConfig: RegionConfig) => {
      const { region, count } = regionConfig;

      return value.some((platformScope) => {
        if (platformScope.platform !== platform) {
          return false;
        }

        return platformScope.regionStoreScopes.some((regionScope) => {
          if (regionScope.region === 'all' && (regionScope.storeIds || []).length === 0) {
            return true;
          }

          if (regionScope.region !== region) {
            return false;
          }

          return (
            regionScope.region === region &&
            ((regionScope.storeIds || []).length === 0 ||
              (regionScope.storeIds || []).length === count)
          );
        });
      });
    },
    [value],
  );

  const isRegionIndetermintate = useCallback(
    (platform: General.PlatformUpperCaseTunnel, regionConfig: RegionConfig) => {
      const { region } = regionConfig;

      return value.some((platformScope) => {
        if (platformScope.platform !== platform) {
          return false;
        }

        return platformScope.regionStoreScopes.some((regionScope) => {
          return regionScope.region === region;
        });
      });
    },
    [value],
  );

  const onPlatformCheck = useCallback(
    (
      checked: boolean,
      props: {
        platform: General.PlatformUpperCaseTunnel;
        count: number;
      },
    ) => {
      const { platform, count: storeCount } = props;

      const next = value.filter((platformScope) => platformScope.platform !== platform);

      if (checked) {
        next.push({
          platform,
          storeCount,
          regionStoreScopes: [
            {
              region: 'all',
              storeCount,
              storeIds: [],
            },
          ],
        });
      }

      onChange(next);
    },
    [value, onChange],
  );

  const onRegionCheck = useCallback(
    (
      checked: boolean,
      props: {
        platform: General.PlatformUpperCaseTunnel;
        region: General.UpperCaseRegion;
        count: number;
      },
    ) => {
      const { platform, region, count: storeCount } = props;

      const next = produce(value, (value) => {
        const platformTargetIndex = value.findIndex(
          (platformScope) => platformScope.platform === platform,
        );
        const platformTarget = value[platformTargetIndex];

        const checkedRegionScope = {
          region,
          storeCount,
          storeIds: [],
        };

        if (!platformTarget) {
          if (checked) {
            value.push({
              platform,
              storeCount,
              regionStoreScopes: [checkedRegionScope],
            });
          }
        } else {
          const regionTargetIndex = platformTarget.regionStoreScopes.findIndex(
            (regionScope) => regionScope.region === region,
          );
          const regionTarget = platformTarget.regionStoreScopes[regionTargetIndex];

          if (!regionTarget) {
            if (checked) {
              platformTarget.regionStoreScopes.push(checkedRegionScope);
            } else {
              const isPlatformChecked = platformTarget.regionStoreScopes.some((regionScope) => {
                return regionScope.region === 'all' && (regionScope.storeIds || []).length === 0;
              });

              // 平台全选时，不可取消选中单个站点
              if (isPlatformChecked) {
                message.warn(
                  t('department.已选择platform全部，请先取消选中', {
                    platform: platformLabelMap[toLowerCase(platform)],
                  }),
                );
              }
            }
          } else if (checked) {
            regionTarget.storeCount = storeCount;
            regionTarget.storeIds = [];
          } else {
            platformTarget.regionStoreScopes.splice(regionTargetIndex, 1);

            if (platformTarget.regionStoreScopes.length === 0) {
              value.splice(platformTargetIndex, 1);
            }
          }
        }
      });

      onChange(next);
    },
    [value, onChange, t],
  );

  return {
    value,
    onChecked,
    onStoreChecked,
    onWabaChecked,
    isChecked,
    isIndeterminate,
    isPlatformChecked,
    isPlatformIndeterminate,
    isRegionChecked,
    isRegionIndetermintate,
    onPlatformCheck,
    onRegionCheck,
  };
}

export default useCheck;
